import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { MemberInfoValue } from '../../shared/component/headless/input/InputBody';
import { BlackFitBtn } from '../../shared/component/headless/button/BtnBody';
import { useModalStore } from '../../shared/store/portal/modalStore';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import deleteIcon from '../../asset/icon/delete.png';
import { ChangeModalFrame } from '../../shared/component/modal/ChangeModalFrame';
import { useValidation } from '../../shared/hooks/useValidation';
import { useMember } from '../../shared/hooks/useApi/useMember';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import HeadlessInput from '../../shared/component/headless/input/HeadlessInput';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import Space from '../../shared/Space';
import { AlertPosition, AlertType } from '../../shared/enum/EnumRepository';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getBtnName } from '../../shared/constants/btnNames';

interface ModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const ChangeHandleModal: React.FC<ModalProps> = ({ setIsUserInput }) => {
  const { standardModal, isClosing, clearModal } = useModalStore();
  const { user } = useAuth();
  const { isValidHandle, validateHandle } = useValidation();
  const { enqueueAlert } = useStandardAlertStore();
  const { updateHandle } = useMember();
  const [temp, setTemp] = useState(standardModal?.value || "");
  const [error, setError] = useState('');

  const handleValidate = (value: string) => {
    if (value.length === 0) {
      setError('');
      return true;
    }
    
    const errorMessage = validateHandle(value);
    if (errorMessage) {
      setError(errorMessage);
      return false;
    }
    
    setError('');
    return true;
  };

  useEffect(() => {
    if (temp.length > 0) {
      setIsUserInput(true);
    } else {
      setIsUserInput(false);
    }
  }, [temp]);

  if (user == null || standardModal == null) return null;

  const changeHandleInput = (value: string) => {
    const lowerValue = value.toLowerCase();
    setTemp(lowerValue);
    handleValidate(lowerValue);
  };

  const handleSubmit = async () => {
    // 빈 값 체크
    if (temp.length === 0) {
      setError('Please enter a URL name.');
      return;
    }
    if (temp === standardModal.value) {
      setError('Please enter a different URL name.');
      return;
    }
    // 형식 검증
    if (!handleValidate(temp)) {
      return;
    }
    if (!isValidHandle(temp)) {
      return;
    }

    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_CHANGE_LINK_ID'),
      callBack: () => {
        updateHandle({
          id: user.id,
          handle: temp,
        });
      }
    });
  };

  return (
    <ChangeModalFrame
      onClick={(e) => e.stopPropagation()}
      $isClosing={isClosing}
    >
      <ChangeModalComp>
        <HeaderRow>
          <Title>{getModalMessage('CHANGE_LINK_ID_TITLE')}</Title>
          <UniversalIconBtn
            size="small"
            border={false}
            isTransparent
            hoverOpacity
            backgroundImg={deleteIcon}
            onClick={() => clearModal()}
          />
        </HeaderRow>

        <Example>
          www.architrave.work/@ 
          <HighlightText>{temp}</HighlightText>
        </Example>
        <HeadlessInput
          type={'text'}
          value={temp}
          handleChange={(e) => changeHandleInput(e.target.value)}
          placeholder={getModalMessage('ENTER_FIELD', { field: standardModal?.title ?? '' })}
          StyledInput={MemberInfoValue}
        />
        <Space $height="12px">
          {error !== null && <ErrorText>{error}</ErrorText>}
        </Space>
        <BtnContainer>
          <HeadlessBtn
            value={getBtnName('CHANGE')}
            handleClick={handleSubmit}
            StyledBtn={BlackFitBtn}
          />
        </BtnContainer>
      </ChangeModalComp>
    </ChangeModalFrame>
  );
};

const ChangeModalComp = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0.5vw;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5vh;
`;

const Title = styled.h2`
  min-width: 0;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const Example = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  font-style: oblique;
  text-align: left;
  padding-bottom: 8px;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_05};
`;

const HighlightText = styled.span`
  font-style: normal;
  padding-left: 2px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const BtnContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 1vw;

  margin-top: 2.5vh;
`

const ErrorText = styled.p`
  width: 100%;
  color: ${({ theme }) => theme.colors.color_alert_red};
  ${({ theme }) => theme.typography.Body_04};
`;

export default ChangeHandleModal;
